import { getAccessToken, refreshToken } from "Helpers/api";
import { MappingChange, TenantMapping } from "Pages/Mapping/Mapping";

let checkURL = process.env.REACT_APP_API_URL_PCI_COMPLIANCE as string;
const urlhost = window.location.host;
if(urlhost.includes('ev.')){
    checkURL = 'https://pci-compliance.dev.venzagrc.com/api/pci-compliance';
}
if(urlhost.includes('tg.')){
    checkURL = 'https://pci-compliance.stg.venzagrc.com/api/pci-compliance';
}
if(!urlhost.includes('ev.') && !urlhost.includes('tg.') && !urlhost.includes(':8')){
    checkURL = 'https://pci-compliance.prod.venzagrc.com/api/pci-compliance';
}

const baseUrl = checkURL as string;

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${getAccessToken()}`,
});

const authorizedFetch = async (
  url: string,
  options: RequestInit,
  retryCount = 0 // start at 0
): Promise<Response> => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${getAccessToken()}`,
    },
  });

  if (response.status === 401 && retryCount < 3) {
    try {
      await refreshToken();
      return authorizedFetch(url, options, retryCount + 1);
    } catch (err) {
      console.error("Token refresh failed:", err);
      throw err;
    }
  }

  return response;
};


export const getProvidersForTenant = async (
  tenantId: string,
  tenantName: string
) => {
  const url = `${baseUrl}/tenants/providers?tenant_id=${tenantId}&tenant_name=${tenantName}`;
  try {
    const response = await authorizedFetch(url, { method: "GET", headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`API call failed: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching provider list:", error);
    throw error;
  }
};

export const updateMappingStatus = async (
  tenantId: number,
  providerId: number,
  status: 'enable' | 'disable'
) => {
  const url = `${baseUrl}/tenant-provider-mappings/update-status?tenant_id=${tenantId}&provider_id=${providerId}&status=${status}`;

  try {
    const response = await authorizedFetch(url, { method: 'POST', headers: getAuthHeaders() });
    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error updating mapping status:", error);
    throw error;
  }
};

interface TenantInfo {
  id: string | number;
  name: string;
}

interface PropertyMerchantMapping {
  property_id?: string;
  property_name: string;
  mid?: string | null;
}

interface ConfirmMappingRequest {
  provider_id: number | null;
  tenant: TenantInfo;
  property_merchant_mappings: PropertyMerchantMapping[];
}

export const confirmMapping = async (
  tenantInfo: TenantMapping,
  propertyMerchantMappings: PropertyMerchantMapping[]
) => {
  const url = `${baseUrl}/property-merchant-mappings`;

  const payload: ConfirmMappingRequest = {
    provider_id: tenantInfo.providerId,
    tenant: {
      id: tenantInfo.id,
      name: tenantInfo.name,
    },
    property_merchant_mappings: propertyMerchantMappings,
  };

  try {
    const response = await authorizedFetch(url, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw {
        status: response.status,
        data: errorBody,
      };
    }

    return response.json();
  } catch (error: any) {
    console.error("Error confirming mapping:", error);
    throw {
      status: error?.status || 500,
      data: { message: error?.data?.errors[0] ?? "Unknown error occurred" },
    };
  }

};


export interface PciPortalProperty {
  mid: string;
  name: string;
  property_id: string | null;
}

export const getExternalMerchants = async (
  tenantId: number,
  providerId: number,
  merchantName?: string,
  mid?: string,
): Promise<PciPortalProperty[]> => {
  const url = `${baseUrl}/external-merchants?tenant_id=${tenantId}&provider_id=${providerId}&merchant_name=${merchantName}&mid=${mid ?? null}&status=unmapped`;

  try {
    const response = await authorizedFetch(url, { method: 'GET', headers: getAuthHeaders(), });
    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    const responseData = await response.json();
    return responseData.data.map((item: { merchant_name: string; external_mid: string; property_id: string | null; }) => ({
      name: item.merchant_name,
      mid: item.external_mid,
      property_id: item.property_id,
    }));
  } catch (error) {
    console.error("Error fetching external merchants:", error);
    throw error;
  }
};

export const getExistingMappedMerchants = async (
  tenantId: number,
  providerId: number
): Promise<MappingChange[]> => {
  const url = `${baseUrl}/property-merchant-mappings?tenant_id=${tenantId}&provider_id=${providerId}&status=active`;

  try {
    const response = await authorizedFetch(url, { method: 'GET', headers: getAuthHeaders(), });
    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`);
    }
    const responseData = await response.json();
    return [...responseData.data.property_merchant_mapping.map((item: { merchant_name: string; external_mid: string; property_id: string | null; property_name: string | null; external_merchant_name: string | null; }) => ({
      id: String(item.property_id),
      venzaProperty: item.property_name,
      pciPortalProperty: item.external_merchant_name,
      venzaPropertyId: item.property_id,
      pciPortalPropertyId: item.external_mid,
      changeType: "unchanged",
      action: null
    })), {
      id: "add-row", venzaProperty: "",
      venzaPropertyId: null,
      pciPortalPropertyId: null, pciPortalProperty: "", changeType: "unchanged", action: "", isAddRow: true
    }];

  } catch (error) {
    console.error("Error fetching external merchants:", error);
    throw error;
  }
};